import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../hooks/useLanguage';
import {
  ShieldIcon, FileTextIcon, CheckIcon,
  ChevronDownIcon, ChevronUpIcon,
  LockIcon, ScaleIcon, MenuIcon, CloseIcon,
  AlertIcon, ClipboardIcon, TrendingUpIcon,
  ArrowRightIcon, TrashIcon, HomeIcon, UserIcon,
  RefreshCcwIcon
} from '../components/Icons';
import AnonymousUpload, { AnonymousUploadRef } from '../components/AnonymousUpload';
import { useAuth } from '../contexts/AuthContext';
import { documentAPI, trackingAPI } from '../services/api';
import { getOrCreateTrackingSessionId } from '../services/tracking';
import { SubscriptionTier } from '../services/api';
import { TIER_FEATURES } from '../constants/subscription';
import './Landing.css';
import './DocumentDetailNew.css';
import '../components/NotificationSettings.css';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['landing', 'common', 'documents']);
  const { getLocalizedPath } = useLanguage();
  const { user, loading } = useAuth();
  const [accountRouting, setAccountRouting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [storyLit, setStoryLit] = useState(false);
  const storyRef = useRef<HTMLElement | null>(null);
  const howItWorksTimelineRef = useRef<HTMLDivElement | null>(null);

  // Resultado Real section - interactive preview states
  const [rrUnderstood, setRrUnderstood] = useState(false);
  const [rrAddedToNegotiation, setRrAddedToNegotiation] = useState(false);
  const [rrDeepOpen, setRrDeepOpen] = useState(false);
  const [rrActiveDeepTab, setRrActiveDeepTab] = useState<'market' | 'negotiation' | 'worst'>('market');
  const [rrLegalOpen, setRrLegalOpen] = useState(false);
  const [rrScenariosOpen, setRrScenariosOpen] = useState(false);

  // Role selection modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'tenant' | 'landlord' | null>(null);
  const uploadRef = useRef<AnonymousUploadRef>(null);
  const roleMotivations = t('landing:roleModal.motivations', { returnObjects: true }) as unknown as string[];
  const [roleMotivation] = useState<string>(() => {
    if (Array.isArray(roleMotivations) && roleMotivations.length > 0) {
      const idx = Math.floor(Math.random() * roleMotivations.length);
      return roleMotivations[idx];
    }
    return '';
  });

  // Typing animation for hero title
  const typingWords = t('landing:hero.typingWords', { returnObjects: true }) as unknown as string[];
  const [typingWordIndex, setTypingWordIndex] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Re-sync shine-line animations when crossing the 768px breakpoint.
  // Switching between fileTravel and fileTravelVertical restarts the file icon's
  // animation cycle while the pen's signingPenAppear keeps its old offset → desync.
  // Resetting animationName briefly forces all child animations to restart together.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const resync = () => {
      const shineLine = document.querySelector('.how-it-works-shine-line');
      if (!shineLine) return;
      const els = [shineLine, ...Array.from(shineLine.querySelectorAll('*'))] as HTMLElement[];
      els.forEach(el => { el.style.animationName = 'none'; });
      void (shineLine as HTMLElement).offsetHeight; // force reflow
      els.forEach(el => { el.style.animationName = ''; });
    };
    mq.addEventListener('change', resync);
    return () => mq.removeEventListener('change', resync);
  }, []);

  // Reset typing animation when language changes
  useEffect(() => {
    setTypingWordIndex(0);
    setTypingText('');
    setIsDeleting(false);
  }, [i18n.language]);

  useEffect(() => {
    if (!Array.isArray(typingWords) || typingWords.length === 0) return;

    const currentWord = typingWords[typingWordIndex % typingWords.length];
    if (!currentWord) return;

    const typingSpeed = isDeleting ? 40 : 80;
    const pauseDelay = isDeleting ? 100 : 2000;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (typingText.length < currentWord.length) {
          setTypingText(currentWord.slice(0, typingText.length + 1));
        } else {
          // Finished typing, pause then delete
          setTimeout(() => setIsDeleting(true), pauseDelay);
        }
      } else {
        // Deleting
        if (typingText.length > 0) {
          setTypingText(currentWord.slice(0, typingText.length - 1));
        } else {
          // Finished deleting, move to next word
          setIsDeleting(false);
          setTypingWordIndex((prev) => (prev + 1) % typingWords.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [typingText, isDeleting, typingWordIndex, typingWords]);

  const freeLimits = TIER_FEATURES[SubscriptionTier.FREE];
  const premiumLimits = TIER_FEATURES[SubscriptionTier.PREMIUM];
  const platinumLimits = TIER_FEATURES[SubscriptionTier.PLATINUM];
  const benefitCardTitles = t('landing:benefitsHighlight.cards.titles', { returnObjects: true }) as unknown as string[];
  const benefitCardDescriptions = t('landing:benefitsHighlight.cards.descriptions', { returnObjects: true }) as unknown as string[];
  const benefitCards = [
    { icon: <AlertIcon size={24} /> },
    { icon: <LockIcon size={24} /> },
    { icon: <TrendingUpIcon size={24} /> },
    { icon: <ClipboardIcon size={24} /> },
  ];
  const [flippedCards, setFlippedCards] = useState<boolean[]>([false, false, false, false]);

  const uploadTrustPoints = [
    { icon: <ScaleIcon size={16} />, text: t('landing:upload.trust.law') },
    { icon: <UserIcon size={16} />, text: t('landing:upload.trust.disclaimer') },
  ];
  const benefitHighlightKeywords = t('landing:benefitsHighlight.highlightKeywords', { returnObjects: true }) as unknown as string[];

  const handleMyAccountClick = async () => {
    if (accountRouting) return;

    setAccountRouting(true);
    try {
      const res = await documentAPI.list();
      const destination = res.data.total > 0 || (res.data.documents?.length ?? 0) > 0
        ? '/catalog'
        : '/upload';
      navigate(getLocalizedPath(destination));
    } catch (err) {
      console.error('Failed to decide account destination:', err);
      navigate(getLocalizedPath('/catalog'));
    } finally {
      setAccountRouting(false);
    }
  };

  const highlightBenefitText = (text: string) => {
    const keywords = Array.isArray(benefitHighlightKeywords) ? benefitHighlightKeywords : [];
    if (keywords.length === 0) return text;
    const escaped = keywords.map((k) => k.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.split(regex).map((part, idx) => (
      keywords.some((k) => k.toLowerCase() === part.toLowerCase())
        ? <span key={`benefit-hl-${idx}`} className="benefit-highlight-key">{part}</span>
        : part
    ));
  };

  const toggleCard = (index: number) => {
    setFlippedCards((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };
  const heroScreenshotDesktop = i18n.language?.startsWith('en') ? '/landing/landing_desktop_en.png' : '/landing/landing_desktop_pt.png';
  const heroScreenshotMobile = i18n.language?.startsWith('en') ? '/landing/landing_mobile_en.png' : '/landing/landing_mobile_pt.png';

  const getUserInitials = (name?: string | null) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase() || '').join('') || 'U';
  };

  const trackFunnelStep = (step: 'started' | 'role_selected' | 'file_selected' | 'upload_completed', role?: 'tenant' | 'landlord', buttonLocation?: string) => {
    const sessionId = localStorage.getItem('tracking_session_id') || undefined;
    const campaignSource = localStorage.getItem('campaign_source') || undefined;
    trackingAPI.trackUploadFunnel({
      step,
      session_id: sessionId,
      campaign_source: campaignSource,
      role,
      button_location: buttonLocation,
    }).catch(() => { /* ignore tracking errors */ });
  };

  const scrollToAnalyzeNow = (trackingLocation?: string) => {
    // Track the CTA click if a location is provided
    if (trackingLocation) {
      trackCTAClick(trackingLocation);
    }

    // Track funnel start
    trackFunnelStep('started', undefined, trackingLocation);

    // Show role selection modal first
    setShowRoleModal(true);
  };

  const handleRoleSelect = (role: 'tenant' | 'landlord') => {
    // Track role selection
    trackFunnelStep('role_selected', role);

    setSelectedRole(role);
    setShowRoleModal(false);

    // Trigger file selection after role is selected
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current.triggerFileSelect();
      } else {
        // Fallback: try to find and click the file input directly
        const fileInput = document.getElementById('anonymous-file-upload') as HTMLInputElement | null;
        if (fileInput) {
          fileInput.click();
        }
      }
    }, 100);
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const trackCTAClick = (location: string) => {
    // Store in localStorage for later attribution on signup
    localStorage.setItem('signup_source', location);
    const trackingSession = getOrCreateTrackingSessionId();
    trackingAPI.logCtaClick({
      cta_id: location,
      page: 'landing',
      location,
      session_id: trackingSession,
      language: i18n.language,
    }).catch(() => {
      // Swallow tracking errors to avoid breaking UX
    });
  };

  useEffect(() => {
    const el = storyRef.current;
    if (!el) return;

    const checkPosition = () => {
      const rect = el.getBoundingClientRect();
      const triggerLine = window.innerHeight * 0.7; // 70% down the viewport
      const hitsLine = rect.top <= triggerLine && rect.bottom >= 0;
      setStoryLit(hitsLine);
    };

    checkPosition();
    window.addEventListener('scroll', checkPosition, { passive: true });
    window.addEventListener('resize', checkPosition);

    return () => {
      window.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, []);

  useEffect(() => {
    const timeline = howItWorksTimelineRef.current;
    if (!timeline) return;

    const syncMobileTimeline = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        timeline.style.removeProperty('--how-it-works-line-left');
        timeline.style.removeProperty('--how-it-works-step1-top');
        timeline.style.removeProperty('--how-it-works-step2-top');
        timeline.style.removeProperty('--how-it-works-step3-top');
        return;
      }

      const timelineRect = timeline.getBoundingClientRect();
      const nodes = Array.from(timeline.querySelectorAll('.how-it-works-node')) as HTMLElement[];
      const targets = nodes.map((node) => node.querySelector('.node-target')).filter(Boolean) as HTMLElement[];
      if (targets.length < 3) return;

      const firstRect = targets[0].getBoundingClientRect();
      const lineLeft = firstRect.left - timelineRect.left + (firstRect.width / 2);

      const stepTops = targets.map((target) => {
        const rect = target.getBoundingClientRect();
        return rect.top - timelineRect.top + (rect.height / 2);
      });

      timeline.style.setProperty('--how-it-works-line-left', `${lineLeft}px`);
      timeline.style.setProperty('--how-it-works-step1-top', `${stepTops[0]}px`);
      timeline.style.setProperty('--how-it-works-step2-top', `${stepTops[1]}px`);
      timeline.style.setProperty('--how-it-works-step3-top', `${stepTops[2]}px`);

      const animatedEls = Array.from(
        timeline.querySelectorAll('.traveling-file-icon, .signing-pen-icon, .how-it-works-shine-line')
      ) as HTMLElement[];
      animatedEls.forEach((el) => { el.style.animationName = 'none'; });
      void timeline.offsetHeight;
      animatedEls.forEach((el) => { el.style.animationName = ''; });
    };

    let raf = window.requestAnimationFrame(syncMobileTimeline);
    window.addEventListener('resize', syncMobileTimeline);
    window.addEventListener('orientationchange', syncMobileTimeline);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', syncMobileTimeline);
      window.removeEventListener('orientationchange', syncMobileTimeline);
    };
  }, [i18n.language]);

  // Scroll tracking and page exit tracking
  useEffect(() => {
    const pageLoadTime = Date.now();
    const sectionsViewed = new Set<string>();
    let lastSectionReached = 'hero';
    let maxScrollDepth = 0;

    const sectionIds = [
      { id: 'hero', selector: '.hero-section' },
      { id: 'benefits', selector: '.benefits-highlight-section' },
      { id: 'features', selector: '.features-tabs-section' },
      { id: 'upload', selector: '#analisar-agora' },
      { id: 'pricing', selector: '#precos' },
      { id: 'way_beyond', selector: '.way-beyond-section' },
      { id: 'faq', selector: '#ajuda' },
      { id: 'footer', selector: '#sobre' },
    ];

    const checkSectionsInView = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      maxScrollDepth = Math.max(maxScrollDepth, scrollPercent);

      for (const section of sectionIds) {
        const el = document.querySelector(section.selector);
        if (el) {
          const rect = el.getBoundingClientRect();
          const inView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
          if (inView) {
            sectionsViewed.add(section.id);
            lastSectionReached = section.id;
          }
        }
      }
    };

    const sendPageExitData = (trigger: 'beforeunload' | 'visibilitychange' | 'navigation') => {
      const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
      const trackingSession = getOrCreateTrackingSessionId();
      const campaignSource = localStorage.getItem('campaign_source') || undefined;

      // Use sendBeacon for reliable delivery on page exit
      const payload = {
        page: 'landing',
        session_id: trackingSession,
        campaign_source: campaignSource,
        time_on_page_seconds: timeOnPage,
        scroll_depth_percent: maxScrollDepth,
        last_section_reached: lastSectionReached,
        sections_viewed: Array.from(sectionsViewed),
        exit_trigger: trigger,
      };

      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/tracking/page-exit', JSON.stringify(payload));
      } else {
        trackingAPI.trackPageExit(payload).catch(() => {});
      }
    };

    const handleBeforeUnload = () => sendPageExitData('beforeunload');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendPageExitData('visibilitychange');
      }
    };

    // Initial check
    checkSectionsInView();

    window.addEventListener('scroll', checkSectionsInView, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('scroll', checkSectionsInView);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const faqsData = t('landing:faq.items', { returnObjects: true }) as unknown as Array<{ question: string; answer: string[] }>;
  const faqs: Array<{ question: string; answer: React.ReactNode }> = faqsData.map((item, idx) => ({
    question: item.question,
    answer: (
      <>
        {(item.answer || []).map((line, i) => (
          <p key={`${idx}-${i}`}>{line}</p>
        ))}
      </>
    )
  }));

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-container">
          <div className="nav-content">
            <div className="nav-logo">
              <img src="/logo_transp.png" alt="ContratoSeguro" />
            </div>

            {/* Desktop Navigation (hidden per request) */}
            <div className="nav-links desktop-only"></div>
            <div className="nav-right">
              {/* Desktop CTAs */}
              <div className="nav-ctas desktop-only">
                {!loading && user ? (
                  <button
                    onClick={handleMyAccountClick}
                    className="account-cta"
                    disabled={accountRouting}
                  >
                    <span className="account-cta-avatar">
                      {getUserInitials(user.full_name || user.email)}
                    </span>
                    <span className="account-cta-text">
                      <strong>{t('common:nav.myAccount')}</strong>
                      <small>{user.full_name || user.email}</small>
                    </span>
                  </button>
                ) : (
                  <button onClick={() => navigate(getLocalizedPath('/login'))} className="btn-primary-landing">
                    {t('landing:nav.myContractSafe')}
                  </button>
                )}
              </div>

              <LanguageSwitcher variant="compact" className="nav-language-switcher" />

              {/* Mobile Menu Button */}
              <button
                className="mobile-menu-btn mobile-only"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu (hidden per request) */}
          {mobileMenuOpen && (
            <div className="mobile-menu">
              {!loading && user ? (
                <button onClick={handleMyAccountClick} className="btn-primary-landing" disabled={accountRouting}>
                  {t('common:nav.myAccount')}
                </button>
              ) : (
                <button onClick={() => navigate(getLocalizedPath('/login'))} className="btn-primary-landing">
                  {t('landing:nav.myContractSafe')}
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="landing-container">
          <div className="hero-content">
            <div className="hero-left">
              <h1 className="hero-title">
                <span className="hero-title-static">{t('landing:hero.titleStatic')}</span>
                <span className="hero-title-typing">
                  {typingText}
                  <span className="typing-cursor">|</span>
                </span>
              </h1>
              <p className="hero-subtitle">
                {t('landing:hero.subtitle')}
              </p>

              <div className="hero-screenshot hero-screenshot--mobile mobile-only">
                <picture>
                  <source media="(max-width: 768px)" srcSet={heroScreenshotMobile} />
                  <img src={heroScreenshotDesktop} alt={t('landing:hero.title')} />
                </picture>
              </div>

              <div className="hero-ctas">
                {user ? (
                  <button
                    onClick={handleMyAccountClick}
                    className="btn-primary-large"
                    disabled={accountRouting}
                  >
                    {t('landing:hero.myAccount')}
                  </button>
                ) : (
                  <div className="hero-signup-options">
                  <div className="hero-buttons-row">
                    <button
                      onClick={() => scrollToAnalyzeNow('hero_analyze_free')}
                      className="hero-cta-primary"
                    >
                      <FileTextIcon size={20} />
                      {t('landing:howItWorks.cta')}
                    </button>
                  </div>
                  </div>
                )}
              </div>
              <div className="hero-features">
                <div className="hero-feature">
                  <ShieldIcon size={18} />
                  <span>{t('landing:hero.features.secure')}</span>
                </div>
              </div>
            </div>

            <div className="hero-right">
              <div className="hero-mockup">
                <div className="mockup-badge mockup-badge-1">
                  <CheckIcon size={16} />
                  <span>{t('landing:hero.badges.detectsRisks')}</span>
                </div>
                <div className="mockup-badge mockup-badge-2">
                  <FileTextIcon size={16} />
                  <span>{t('landing:hero.badges.explainsClauses')}</span>
                </div>
                <div className="mockup-badge mockup-badge-3">
                  <ScaleIcon size={16} />
                  <span>{t('landing:hero.badges.legalAnalysis')}</span>
                </div>
                <div className="hero-screenshot">
                  <picture>
                    <source media="(max-width: 768px)" srcSet={heroScreenshotMobile} />
                    <img src={heroScreenshotDesktop} alt={t('landing:hero.title')} />
                  </picture>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`story-banner ${storyLit ? 'story-lit' : ''}`} ref={storyRef}>
        <div className="landing-container story-banner-inner">
          <div className="story-badge floating-badge">
            <ShieldIcon size={16} />
            <span>{t('landing:storyBanner.badge')}</span>
          </div>
          <div className="story-copy">
            <p className="story-question">
              {t('landing:storyBanner.question')}
            </p>
            <p className="story-answer">
              {t('landing:storyBanner.answer')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Highlight Section */}
      <section className="benefits-highlight-section">
        <div className="landing-container">
          <div className="benefits-highlight-header">
            <div className="benefits-highlight-counter">
              <div className="benefits-highlight-counter-text">
                {t('landing:benefitsHighlight.titleStrong')}{' '}
                {t('landing:benefitsHighlight.titleRest') && (
                  <span>{t('landing:benefitsHighlight.titleRest')}</span>
                )}
              </div>
            </div>
            <p className="benefits-highlight-intro">
              {t('landing:benefitsHighlight.intro')}
            </p>
          </div>
          <div className="benefits-highlight-grid">
            {benefitCards.map((card, idx) => (
              <div
                className={`benefits-highlight-card ${flippedCards[idx] ? 'is-flipped' : ''}`}
                key={`benefit-card-${idx}`}
                role="button"
                tabIndex={0}
                onClick={() => toggleCard(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCard(idx);
                  }
                }}
                aria-pressed={flippedCards[idx]}
              >
                <div className="benefits-highlight-card-inner">
                  <div className="benefits-highlight-card-face card-front">
                    <div className="benefits-highlight-card-icon">
                      {card.icon}
                    </div>
                    <h3>{benefitCardTitles[idx]}</h3>
                  </div>
                  <div className="benefits-highlight-card-face card-back">
                    <p>{highlightBenefitText(benefitCardDescriptions[idx])}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="benefits-highlight-footer">
            <p className="benefits-highlight-footer-text">
              {t('landing:benefitsHighlight.footerText')}
            </p>
            <button
              type="button"
              className="benefits-highlight-text-link"
              onClick={() => scrollToAnalyzeNow('benefits_ver_meu_contrato')}
            >
              {t('landing:benefitsHighlight.footerCta')} <span className="text-link-arrow">›</span>
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section" id="como-funciona">
        <div className="landing-container">
          <div className="how-it-works-header">
            <div className="how-it-works-badge">
              <div className="benefits-highlight-counter-icon">
                <RefreshCcwIcon size={24} />
              </div>
            </div>
            <h2 className="how-it-works-title">{t('landing:howItWorksSteps.title')}</h2>
          </div>
          <p className="how-it-works-intro">{t('landing:howItWorksSteps.disclaimer')}</p>
          <div className="how-it-works-timeline" ref={howItWorksTimelineRef}>
            {/* Connecting line with traveling file icon */}
            <div className="how-it-works-shine-line">
              <div className="shine-line-track"></div>
              <div className="shine-line-glow"></div>
              {/* Traveling file icon that transforms from dark to clear */}
              <div className="traveling-file-icon">
                <svg viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path className="file-bg" d="M2 4C2 2.89543 2.89543 2 4 2H14L22 10V24C22 25.1046 21.1046 26 20 26H4C2.89543 26 2 25.1046 2 24V4Z" />
                  <path className="file-fold" d="M14 2L14 10H22" />
                  <line className="file-line file-line-1" x1="6" y1="14" x2="18" y2="14" />
                  <line className="file-line file-line-2" x1="6" y1="18" x2="16" y2="18" />
                  <line className="file-line file-line-3" x1="6" y1="22" x2="14" y2="22" />
                </svg>
              </div>
              {/* Minimal pen signing at the end */}
              <div className="signing-pen-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="presentation">
                  <path className="pen-body" d="M17 3a2.12 2.12 0 0 1 3 3L7.5 18.5 3 19l.5-4.5Z" />
                  <path className="pen-detail" d="m15 5 3 3" />
                  <path className="pen-detail" d="M8 14l2 2" />
                  <path className="pen-signature" d="M2.2 20.6c1.3 1.7 3 1.9 4.5.4 1.4-1.4 3-1.3 4-.1 1.3 1.5 2.8 1.6 4.6-.4" />
                </svg>
              </div>
            </div>

            {/* Step 1 */}
            <div className="how-it-works-node">
              <div className="node-target">
                <div className="target-outer"></div>
                <div className="target-middle"></div>
                <div className="target-inner"></div>
                <div className="target-core"></div>
              </div>
              <div className="node-content">
                <span className="node-number">01</span>
                <h3 className="node-title">{t('landing:howItWorksSteps.step1.title')}</h3>
                <p className="node-description">{t('landing:howItWorksSteps.step1.subtitle')}</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="how-it-works-node">
              <div className="node-target">
                <div className="target-outer"></div>
                <div className="target-middle"></div>
                <div className="target-inner"></div>
                <div className="target-core"></div>
              </div>
              <div className="node-content">
                <span className="node-number">02</span>
                <h3 className="node-title">{t('landing:howItWorksSteps.step2.title')}</h3>
                <p className="node-description">{t('landing:howItWorksSteps.step2.subtitle')}</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="how-it-works-node">
              <div className="node-target">
                <div className="target-outer"></div>
                <div className="target-middle"></div>
                <div className="target-inner"></div>
                <div className="target-core"></div>
              </div>
              <div className="node-content">
                <span className="node-number">03</span>
                <h3 className="node-title">{t('landing:howItWorksSteps.step3.title')}</h3>
                <p className="node-description">{t('landing:howItWorksSteps.step3.subtitle')}</p>
              </div>
            </div>
          </div>
            <div className="how-it-works-cta">
              <button
                type="button"
                className="how-it-works-cta-btn"
                onClick={() => scrollToAnalyzeNow('how_it_works_analyze_free')}
              >
                {t('landing:howItWorksSteps.cta')}
              </button>
            </div>
        </div>
      </section>
      {/* Resultado Real (Preview) */}
      <section className="resultado-real-section">
        <div className="landing-container">
          <div className="section-header">
            <h2 className="section-title">{t('landing:resultadoReal.title')}</h2>
            <p className="section-subtitle">{t('landing:resultadoReal.subtitle')}</p>
          </div>

          <div className="rr-body">
          <div className="rr-analysis-summary">
            <div className="rr-analysis-header">
              <div>
                <h3>{t('documents:detail.risks.title')}</h3>
                <p>{t('documents:detail.risks.subtitle')}</p>
              </div>
            </div>

            <div className="risk-one-liner">
              <span className="risk-one-liner-count">
                {t('documents:detail.risks.oneliner.points', { count: 3 })}
              </span>
              <span className="risk-one-liner-sep">·</span>
              <span className="risk-one-liner-critical">
                {t('documents:detail.risks.oneliner.critical', { count: 2 })}
              </span>
              <span className="risk-one-liner-sep">·</span>
              <span className="risk-one-liner-impact">
                {t('documents:detail.risks.oneliner.estimatedImpact', { amount: t('landing:resultadoReal.summaryAmount') })}
              </span>
            </div>

            <div className="risk-filter-chips">
              <button type="button" className="risk-filter-chip">
                {t('documents:detail.risks.filterChips.all', { count: 3 })}
              </button>
              <button
                type="button"
                className="risk-filter-chip chip-high active"
                onClick={() => {
                  setTimeout(() => {
                    document.getElementById('rr-risk-0')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 80);
                }}
              >
                {t('documents:detail.risks.filterChips.critical', { count: 2 })}
              </button>
              <button
                type="button"
                className="risk-filter-chip chip-medium"
                onClick={() => {
                  setTimeout(() => {
                    document.getElementById('rr-risk-1')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 80);
                }}
              >
                {t('documents:detail.risks.filterChips.moderate', { count: 1 })}
              </button>
              <button type="button" className="risk-filter-chip chip-low" disabled>
                {t('documents:detail.risks.filterChips.low', { count: 0 })}
              </button>
            </div>
          </div>

          {rrAddedToNegotiation && (
            <div className="risk-draft-chip">
              <span className="risk-draft-chip-dot">●</span>
              <span>
                {t('documents:detail.risks.draftChip.label')} · <strong>{t('documents:detail.risks.draftChip.points', { count: 1 })}</strong>
              </span>
              <button
                type="button"
                className="risk-draft-chip-action"
                onClick={() => {
                  setTimeout(() => {
                    document.getElementById('rr-risk-0')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 80);
                }}
              >
                {t('documents:detail.risks.draftChip.view')}
              </button>
            </div>
          )}

          {/* Detailed risk card preview - mirrors the real document page */}
          <div className="rr-details">
              <div className="rr-real-preview">
                <div id="rr-risk-0" className="risk-tab risk-tab--high rr-risk-tab">
                  <div className="risk-tab-inner">
                    <div className="risk-tab-head">
                      <h3 className="risk-tab-title">{t('landing:resultadoReal.risk1.title')}</h3>
                      <button
                        type="button"
                        className={`risk-tab-understood${rrUnderstood ? ' is-checked' : ''}`}
                        onClick={() => setRrUnderstood(p => !p)}
                        aria-pressed={rrUnderstood}
                      >
                        <CheckIcon size={12} />
                        <span>{rrUnderstood ? t('landing:resultadoReal.preview.understoodDone') : t('landing:resultadoReal.preview.understood')}</span>
                      </button>
                    </div>

                    <div className="risk-tab-severity-line">
                      <span className="risk-sev-dot risk-sev-dot--high">●</span>
                      <span className="risk-sev-label">{t('common:riskLevels.high')}</span>
                      <span className="risk-sev-sep">·</span>
                      <span className="risk-sev-financial">{t('landing:resultadoReal.risk1.financialImpact')}</span>
                      <span className="risk-sev-sep">·</span>
                      <span className="risk-sev-neg">{t('landing:resultadoReal.preview.negotiability')}</span>
                    </div>

                    <p className="risk-tab-impact">{t('landing:resultadoReal.risk1.impact')}</p>
                    <p className="risk-tab-why-it-matters">{t('landing:resultadoReal.risk1.whyItMatters')}</p>

                    <div className="risk-tab-actions">
                      <p className="risk-tab-actions-heading">{t('landing:resultadoReal.preview.whatYouCanDo')}</p>
                      <ul className="risk-tab-actions-list">
                        {(t('landing:resultadoReal.risk1.actions', { returnObjects: true }) as unknown as string[]).map((item) => (
                          <li key={item} className="risk-action-row">
                            <span className="risk-action-text">{item}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        className="risk-add-all-btn"
                        onClick={() => setRrAddedToNegotiation(p => !p)}
                      >
                        {rrAddedToNegotiation
                          ? t('landing:resultadoReal.preview.addedToNegotiation')
                          : t('landing:resultadoReal.preview.addToNegotiation')}
                      </button>
                    </div>

                    {rrAddedToNegotiation && (
                      <div className="rr-demo-result" role="status">
                        {t('landing:resultadoReal.preview.negotiationResult')}
                      </div>
                    )}

                    <div className="risk-deep-promo">
                      {!rrDeepOpen && (
                        <p className="risk-deep-promo-teaser">{t('landing:resultadoReal.preview.deepTeaser')}</p>
                      )}
                      <button
                        type="button"
                        className="risk-deep-promo-btn"
                        onClick={() => setRrDeepOpen(p => !p)}
                      >
                        {rrDeepOpen ? t('landing:resultadoReal.preview.closeAnalysis') : t('landing:resultadoReal.preview.openAnalysis')}
                      </button>
                    </div>

                    {rrDeepOpen && (
                      <div className="risk-deep-section">
                        <div className="deep-ai-header">
                          <span className="deep-ai-badge">✦</span>
                          <span className="deep-ai-label">{t('landing:resultadoReal.preview.fullAnalysis')}</span>
                        </div>

                        <div className="deep-tabs" role="tablist">
                          {(['market', 'negotiation', 'worst'] as const).map((tab) => (
                            <button
                              key={tab}
                              type="button"
                              role="tab"
                              aria-selected={rrActiveDeepTab === tab}
                              className={`deep-tab${rrActiveDeepTab === tab ? ' is-active' : ''}`}
                              onClick={() => setRrActiveDeepTab(tab)}
                            >
                              {t(`landing:resultadoReal.preview.tabs.${tab}`)}
                            </button>
                          ))}
                        </div>

                        {rrActiveDeepTab === 'market' && (
                          <div className="deep-panel" role="tabpanel">
                            <div className="deep-metric-pill deep-metric-pill--red">
                              {t('landing:resultadoReal.preview.marketPill')}
                            </div>
                            <div className="deep-market-bars">
                              <div className="deep-market-row">
                                <span className="deep-market-row-label">{t('landing:resultadoReal.preview.marketBaseline')}</span>
                                <div className="deep-bar-track">
                                  <div className="deep-bar-fill deep-bar-fill--market" style={{ width: '100%' }} />
                                </div>
                                <span className="deep-market-row-value">{t('landing:resultadoReal.preview.marketValue')}</span>
                              </div>
                              <div className="deep-market-row">
                                <span className="deep-market-row-label">{t('landing:resultadoReal.preview.thisContract')}</span>
                                <div className="deep-bar-track">
                                  <div className="deep-bar-fill" style={{ width: '30%', background: 'var(--risk-crit, #E11D48)' }} />
                                </div>
                                <span className="deep-market-row-value">{t('landing:resultadoReal.preview.contractValue')}</span>
                              </div>
                              <div className="deep-market-delta deep-market-delta--bad">
                                {t('landing:resultadoReal.preview.marketDelta')}
                              </div>
                            </div>
                            <p className="deep-panel-text">{t('landing:resultadoReal.preview.marketExplanation')}</p>
                          </div>
                        )}

                        {rrActiveDeepTab === 'negotiation' && (
                          <div className="deep-panel" role="tabpanel">
                            <div className="deep-strength deep-strength--alta">
                              <div className="deep-metric-pill deep-metric-pill--inherit">
                                {t('landing:resultadoReal.preview.negotiationLevel')}
                              </div>
                              <div className="deep-strength-meter" role="meter" aria-valuemin={0} aria-valuemax={10} aria-valuenow={8}>
                                {Array.from({ length: 10 }, (_, i) => (
                                  <span key={i} className={`deep-meter-dot${i < 8 ? ' is-filled' : ''}`} />
                                ))}
                              </div>
                              <span className="deep-strength-sublabel">{t('landing:resultadoReal.preview.negotiationStrength')}</span>
                            </div>
                            <p className="deep-panel-text">{t('landing:resultadoReal.preview.negotiationExplanation')}</p>
                          </div>
                        )}

                        {rrActiveDeepTab === 'worst' && (
                          <div className="deep-panel" role="tabpanel">
                            <div className="deep-metric-pill deep-metric-pill--amber">
                              {t('landing:resultadoReal.preview.tabs.worst')}
                            </div>
                            <p className="deep-panel-text">{t('landing:resultadoReal.preview.worstCase')}</p>
                            <div className="deep-example">
                              <span className="deep-example-label">{t('landing:resultadoReal.preview.exampleLabel')}</span>
                              <p className="deep-example-text">{t('landing:resultadoReal.preview.worstExample')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <details className="risk-fundamentos" open={rrLegalOpen} onToggle={(e) => setRrLegalOpen(e.currentTarget.open)}>
                      <summary className="risk-fundamentos-summary">
                        <span>{t('landing:resultadoReal.preview.legalFoundations')}</span>
                        <span className="risk-fundamentos-meta">{t('landing:resultadoReal.preview.legalMeta')}</span>
                        <ChevronDownIcon size={13} className="risk-fundamentos-chevron" />
                      </summary>
                      <div className="risk-fundamentos-body">
                        <div className="risk-tab-detail-group">
                          <p className="risk-tab-detail-label">{t('landing:features.risks.contractClause')}</p>
                          <div className="risk-tab-clause">{t('landing:resultadoReal.risk1.clause')}</div>
                        </div>
                        <div className="risk-tab-detail-group">
                          <p className="risk-tab-detail-label">{t('landing:features.risks.legalBasis')}</p>
                          <p className="risk-tab-detail-text">{t('landing:resultadoReal.risk1.legalBasis')}</p>
                          <a
                            href="https://www.planalto.gov.br/ccivil_03/leis/l8245.htm#art4"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="legal-reference-link"
                          >
                            {t('landing:resultadoReal.preview.openLegalReference')}
                          </a>
                        </div>
                      </div>
                    </details>

                    <div className="risk-tab-footer">
                      <button
                        type="button"
                        className="risk-tab-show-details"
                        onClick={() => setRrScenariosOpen(p => !p)}
                      >
                        <span>{rrScenariosOpen ? t('landing:resultadoReal.preview.hideScenarios') : t('landing:resultadoReal.preview.showScenarios')}</span>
                        {rrScenariosOpen ? <ChevronUpIcon size={13} /> : <ChevronDownIcon size={13} />}
                      </button>
                    </div>

                    {rrScenariosOpen && (
                      <div className="risk-tab-details">
                        <div className="risk-tab-detail-group">
                          <p className="risk-tab-detail-label">{t('landing:resultadoReal.preview.whenItHappens')}</p>
                          <ul className="risk-tab-detail-list">
                            {(t('landing:resultadoReal.risk1.scenarios', { returnObjects: true }) as unknown as string[]).map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rr-card2-wrap">
                  <div id="rr-risk-1" className="risk-tab risk-tab--high rr-risk-tab">
                    <div className="risk-tab-inner">
                      <div className="risk-tab-head">
                        <h3 className="risk-tab-title">{t('landing:resultadoReal.risk2.title')}</h3>
                      </div>
                      <div className="risk-tab-severity-line">
                        <span className="risk-sev-dot risk-sev-dot--high">●</span>
                        <span className="risk-sev-label">{t('common:riskLevels.high')}</span>
                        <span className="risk-sev-sep">·</span>
                        <span className="risk-sev-financial">{t('landing:resultadoReal.risk2.financialImpact')}</span>
                      </div>
                      <p className="risk-tab-impact">{t('landing:resultadoReal.risk2.impact')}</p>
                    </div>
                  </div>
                  <div className="rr-preview-fade">
                    <button
                      type="button"
                      className="rr-analyze-cta"
                      onClick={() => scrollToAnalyzeNow('rr_preview_cta')}
                    >
                      {t('landing:resultadoReal.cta')}
                      <ArrowRightIcon size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>{/* /rr-body */}
        </div>
      </section>

      {/* Founder Story */}
      <section className="founder-section">
        <div className="landing-container">
          <div className="founder-panel">
            {/* Desktop: sticky photo in left grid column */}
            <div className="founder-photo-wrap founder-photo-desktop">
              <img src="/portrait.png" alt="Vadym Polianskyi" className="founder-photo" />
              <div className="founder-caption">
                <span className="founder-caption-name">Vadym Polianskyi</span>
                <span className="founder-caption-role">{t('landing:founder.role')}</span>
              </div>
            </div>
            <div className="founder-text-wrap">
              <h3 className="founder-section-title">{t('landing:founder.title')}</h3>
              {/* Mobile: photo sits between title and body paragraphs */}
              <div className="founder-photo-wrap founder-photo-mobile">
                <img src="/portrait.png" alt="Vadym Polianskyi" className="founder-photo" />
                <div className="founder-caption">
                  <span className="founder-caption-name">Vadym Polianskyi</span>
                  <span className="founder-caption-role">{t('landing:founder.role')}</span>
                </div>
              </div>
              <div className="founder-body">
                <p>{t('landing:founder.p2')}</p>
                <p className="founder-emotion">{t('landing:founder.p3')}</p>
                <p>{t('landing:founder.p4')}</p>
                <p>{t('landing:founder.p5')}</p>
                <p>{t('landing:founder.p6')}</p>
                <p>{t('landing:founder.p7')}</p>
                <p>{t('landing:founder.p8')}</p>
              </div>
              <p className="founder-disclaimer">{t('landing:founder.disclaimer')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Anonymous Upload Section - simplified & clear */}
      <section
        id="analisar-agora"
        className="anonymous-upload-section"
        style={{
          padding: '4.5rem 0',
          background: 'linear-gradient(135deg, rgb(136 255 149) 0%, rgb(141 167 255) 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div className="landing-container" style={{ maxWidth: '1100px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '2.25rem' }}>
            <div className="section-badge">
              {t('landing:upload.badge')}
            </div>
            <h2
              style={{
                fontSize: '2.25rem',
                fontWeight: 800,
                color: '#0f172a',
                marginTop: '1rem',
                marginBottom: '0.5rem'
              }}
            >
              {t('landing:upload.title')}
            </h2>
            <p
              style={{
                fontSize: '1.05rem',
                color: '#475569',
                maxWidth: '720px',
                margin: '0 auto'
              }}
            >
              {t('landing:upload.subtitle')}
            </p>
          </div>

          <div
            className="upload-section-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.05fr 0.95fr',
              gap: '1.5rem',
              alignItems: 'stretch'
            }}
          >
            <div
              className="upload-info-card"
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '1.25rem',
                padding: '1.75rem',
                boxShadow: '0 22px 55px rgba(15, 23, 42, 0.12)',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}
            >
              <p style={{ fontSize: '1.05rem', color: '#0f172a', fontWeight: 700, marginBottom: '0.25rem' }}>
                {t('landing:upload.whatYouGet')}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(t('landing:upload.benefits', { returnObjects: true }) as unknown as string[]).map((item, idx) => (
                  <li
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.65rem',
                      color: '#334155',
                      fontSize: '1rem',
                      lineHeight: 1.5
                    }}
                  >
                  <CheckIcon size={20} style={{ color: '#0c8b5c', flexShrink: 0, marginTop: '2px' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="upload-trust-strip">
                {uploadTrustPoints.map((point) => (
                  <div key={point.text} className="upload-trust-chip">
                    {point.icon}
                    <span>{point.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="upload-component-card"
              style={{
                background: '#ffffff',
                borderRadius: '1.25rem',
                padding: '1.75rem',
                boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
                border: '1px solid #e2e8f0'
              }}
            >
              <AnonymousUpload ref={uploadRef} preSelectedRole={selectedRole} />
            </div>
          </div>
        </div>
      </section>

      {/* Security & Privacy */}
      <section className="privacy-section">
        <div className="landing-container">
          <div className="privacy-inner">
            <div className="section-badge">
              <ShieldIcon size={13} />
              {t('landing:privacy.badge')}
            </div>
            <h2 className="privacy-title">{t('landing:privacy.title')}</h2>
            <div className="privacy-grid">
              {(() => {
                const items = t('landing:privacy.items', { returnObjects: true }) as unknown as string[];
                const icons = [
                  <LockIcon size={20} />,
                  <TrashIcon size={20} />,
                  <ShieldIcon size={20} />,
                  <ScaleIcon size={20} />,
                ];
                return items.map((item, i) => (
                  <div key={i} className="privacy-item">
                    <div className="privacy-item-icon">{icons[i]}</div>
                    <p className="privacy-item-text">{item}</p>
                  </div>
                ));
              })()}
            </div>
            <p className="privacy-footnote">{t('landing:privacy.footnote')}</p>
          </div>
        </div>
      </section>

      {/* Pricing temporarily disabled */}
      {false && (
        <section id="precos" className="pricing-section">
          <div className="landing-container">
            <h2 className="section-title">{t('landing:pricing.title')}</h2>

            <div className="pricing-grid">
              <div className="pricing-card">
                <h3 className="pricing-name">{t('landing:pricing.plans.free.name')}</h3>
                <p className="pricing-tagline">{t('landing:pricing.plans.free.tagline')}</p>
                <div className="pricing-price">
                  <span className="price-value">R$ 0</span>
                  <span className="price-period">{t('landing:pricing.always')}</span>
                </div>
                <div className="pricing-groups">
                  <div className="pricing-group">
                    <div className="pricing-group-title">{t('landing:pricing.includes')}</div>
                    <div className="pricing-lines">
                      {(t('landing:pricing.plans.free.features', { returnObjects: true }) as unknown as string[]).map((feature, idx) => (
                        <div className="pricing-line" key={idx}>
                          <CheckIcon size={18} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pricing-group">
                    <div className="pricing-group-title">{t('landing:pricing.limits')}</div>
                    <div className="pricing-limits">
                      <div className="pricing-limit">{t('landing:pricing.contractsPerMonth', { count: freeLimits.contractsPerMonth })}</div>
                      <div className="pricing-limit">{t('landing:pricing.questionsPerContract', { count: freeLimits.questionsPerContractMonth })}</div>
                    </div>
                  </div>
                </div>
                <button onClick={() => {
                  trackCTAClick('pricing_free');
                  navigate(getLocalizedPath('/signup'));
                }} className="btn-primary-landing">
                  {t('landing:pricing.plans.free.cta')}
                </button>
              </div>

              <div className="pricing-card pricing-popular">
                <div className="popular-badge">{t('landing:pricing.popular')}</div>
                <h3 className="pricing-name">{t('landing:pricing.plans.premium.name')}</h3>
                <p className="pricing-tagline">{t('landing:pricing.plans.premium.tagline')}</p>
                <div className="pricing-price">
                  <span className="price-value">R$ 5</span>
                  <span className="price-period">{t('landing:pricing.perMonth')}</span>
                </div>
                <div className="pricing-groups">
                  <div className="pricing-group">
                    <div className="pricing-group-title">{t('landing:pricing.includes')}</div>
                    <div className="pricing-lines">
                      {(t('landing:pricing.plans.premium.features', { returnObjects: true }) as unknown as string[]).map((feature, idx) => (
                        <div className="pricing-line" key={idx}>
                          <CheckIcon size={18} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pricing-group">
                    <div className="pricing-group-title">{t('landing:pricing.limits')}</div>
                    <div className="pricing-limits">
                      <div className="pricing-limit">{t('landing:pricing.contractsPerMonth', { count: premiumLimits.contractsPerMonth })}</div>
                      <div className="pricing-limit">{t('landing:pricing.questionsPerContract_plural', { count: premiumLimits.questionsPerContractMonth })}</div>
                    </div>
                  </div>
                </div>
                <button onClick={() => {
                  trackCTAClick('pricing_premium');
                  navigate(getLocalizedPath('/signup'));
                }} className="btn-primary-landing">
                  {t('landing:pricing.plans.premium.cta')}
                </button>
              </div>

              <div className="pricing-card">
                <h3 className="pricing-name">{t('landing:pricing.plans.platinum.name')}</h3>
                <p className="pricing-tagline">{t('landing:pricing.plans.platinum.tagline')}</p>
                <div className="pricing-price">
                  <span className="price-value">R$ 20</span>
                  <span className="price-period">{t('landing:pricing.perMonth')}</span>
                </div>
                <div className="pricing-groups">
                  <div className="pricing-group">
                    <div className="pricing-group-title">{t('landing:pricing.includes')}</div>
                    <div className="pricing-lines">
                      {(t('landing:pricing.plans.platinum.features', { returnObjects: true }) as unknown as string[]).map((feature, idx) => (
                        <div className="pricing-line" key={idx}>
                          <CheckIcon size={18} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pricing-group">
                    <div className="pricing-group-title">{t('landing:pricing.limits')}</div>
                    <div className="pricing-limits">
                      <div className="pricing-limit">{t('landing:pricing.contractsPerMonth', { count: platinumLimits.contractsPerMonth })}</div>
                      <div className="pricing-limit">{t('landing:pricing.questionsPerContract_plural', { count: platinumLimits.questionsPerContractMonth })}</div>
                    </div>
                  </div>
                </div>
                <button onClick={() => {
                  trackCTAClick('pricing_platinum');
                  navigate(getLocalizedPath('/signup'));
                }} className="btn-primary-landing">
                  {t('landing:pricing.plans.platinum.cta')}
                </button>
              </div>
            </div>

            <p className="pricing-disclaimer">
              <LockIcon size={16} /> {t('landing:pricing.disclaimer')}
            </p>
            <p className="pricing-note">
              {t('landing:pricing.note')}
            </p>
          </div>
        </section>
      )}
      {/* FAQ */}
      <section id="ajuda" className="faq-section">
        <div className="landing-container">
          <h2 className="section-title">{t('landing:faq.title')}</h2>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  {openFaq === index ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    {typeof faq.answer === 'string' ? <p>{faq.answer}</p> : faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Emotional Close */}
      <section className="final-cta-section">
        <div className="final-cta-inner">
          <h2 className="final-cta-title">
            {t('landing:finalCta.titleLine1')}{' '}
            <span className="final-cta-title-accent">{t('landing:finalCta.titleLine2')}</span>
          </h2>
          <p className="final-cta-subtitle">{t('landing:finalCta.subtitle')}</p>
          <button
            type="button"
            className="final-cta-btn"
            onClick={() => scrollToAnalyzeNow('final_cta')}
          >
            {t('landing:finalCta.cta')}
            <ArrowRightIcon size={18} />
          </button>
          <p className="final-cta-reassurance">{t('landing:finalCta.reassurance')}</p>
        </div>
      </section>

      {/* Footer */}
      <footer id="sobre" className="landing-footer">
        <div className="landing-container">
          <div className="footer-grid">
            <div className="footer-column">
              <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo" />
              <p className="footer-description">
                {t('common:footer.description')}
              </p>
            </div>

            <div className="footer-column">
              <h4>{t('common:footer.quickLinks')}</h4>
              <ul>
                <li><a href="#como-funciona">{t('common:footer.howItWorks')}</a></li>
                {false && <li><a href="#precos">{t('common:footer.pricing')}</a></li>}
                <li><a href="#ajuda">{t('common:footer.help')}</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>{t('common:footer.legal')}</h4>
              <ul>
                <li><a href={getLocalizedPath('/termos-de-uso')}>{t('common:footer.termsOfUse')}</a></li>
                <li><a href={getLocalizedPath('/politica-de-privacidade')}>{t('common:footer.privacyPolicy')}</a></li>
                <li><a href={getLocalizedPath('/politica-de-cookies')}>{t('common:footer.cookiesPolicy')}</a></li>
                <li><a href={getLocalizedPath('/aviso-legal')}>{t('common:footer.legalNotice')}</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>{t('common:footer.contact')}</h4>
              <ul>
                <li>suporte@contratoseguro.com</li>
                <li>lgpd@contratoseguro.com</li>
              </ul>
              <button onClick={() => {
                trackCTAClick('footer');
                scrollToAnalyzeNow('footer_analyze');
              }} className="btn-primary-landing footer-cta">
                {t('landing:howItWorks.cta')}
              </button>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-bottom-row">
              <p>{t('common:footer.copyright', { year: new Date().getFullYear() })}</p>
              <p className="footer-made-in">{t('common:footer.madeInBrazil')}</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Role selection modal - shown before file selection */}
      {showRoleModal && createPortal(
        <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">{t('landing:anonymousUpload.roleTitle')}</h2>
              <p className="modal-subtitle">
                {t('landing:anonymousUpload.roleSubtitle')}
              </p>
              {roleMotivation && <p className="role-motivation">{roleMotivation}</p>}
              <div className="role-buttons">
                <button
                  className="role-button tenant-button"
                  onClick={() => handleRoleSelect('tenant')}
                >
                <div className="role-icon"><UserIcon size={32} /></div>
                <div className="role-title">{t('landing:anonymousUpload.tenantTitle')}</div>
                <div className="role-description">{t('landing:anonymousUpload.tenantDesc')}</div>
              </button>
              <button
                className="role-button landlord-button"
                onClick={() => handleRoleSelect('landlord')}
              >
                <div className="role-icon"><HomeIcon size={32} /></div>
                <div className="role-title">{t('landing:anonymousUpload.landlordTitle')}</div>
                <div className="role-description">{t('landing:anonymousUpload.landlordDesc')}</div>
              </button>
            </div>
            <button
              className="modal-close"
              onClick={() => setShowRoleModal(false)}
            >
              {t('common:buttons.cancel')}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Landing;
